import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import RequestDialog from "@/components/RequestDialog";
import useLivePoll from "@/hooks/useLivePoll";
import LiveDot from "@/components/LiveDot";
import { Plus, Scissors, Truck, Clock, CheckCircle2, XCircle } from "lucide-react";

const BUCKETS = [
  { key: "pending", label: "Pending — waiting to be picked up", icon: Clock },
  { key: "active", label: "In progress — on the bench or at the reference lab", icon: Scissors },
  { key: "done", label: "Completed", icon: CheckCircle2 },
  { key: "cancelled", label: "Cancelled", icon: XCircle },
];

const STATUS_STYLE = {
  Pending: "bg-slate-200 text-slate-800", Requested: "bg-slate-200 text-slate-800",
  "Pending pickup": "bg-slate-200 text-slate-800", "In progress": "bg-amber-700 text-white",
  "In transit": "bg-sky-700 text-white", "Received by lab": "bg-teal-700 text-white",
  "Results received": "bg-green-800 text-white", Completed: "bg-[#134e4a] text-white",
  Cancelled: "bg-red-800 text-white",
};

export default function HistologyRequests() {
  const { can } = useAuth();
  const [data, setData] = useState({ requests: [], counts: {} });
  const [kind, setKind] = useState("");
  const [cases, setCases] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async (silent = false) => {
    try {
      const res = await api.get("/histology-requests", { params: { kind } });
      setData(res.data);
    } catch (e) { if (!silent) toast.error(apiError(e)); }
  }, [kind]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/cases", { params: { limit: 500 } }).then((r) => setCases(r.data)).catch(() => {});
  }, []);
  const lastSync = useLivePoll(() => load(true), 6000);

  const setInhouseStatus = async (row, status) => {
    setBusy(row.id);
    try {
      const { data: updated } = await api.post(`/recuts/${row.id}/status`, { status });
      if (status === "Completed") toast.success(`Cut ${(updated.created_barcodes || []).join(", ")}`);
      else toast.success(`Request marked ${status}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const setSendoutStatus = async (row, sendout_status) => {
    setBusy(row.id);
    try {
      await api.put(`/sendouts/${row.id}`, { sendout_status });
      toast.success(`Send-out marked ${sendout_status}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const canOrder = data.can_order_inhouse || data.can_order_sendout;
  const rows = (b) => (data.requests || []).filter((r) => r.bucket === b);

  return (
    <div className="space-y-6" data-testid="histology-requests-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#134e4a]">TraX Suite — Histology</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Histology Requests
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
            Recuts, levels, special stains and send-outs in one queue — {rows("pending").length} pending,{" "}
            {rows("active").length} in progress. STAT first.
            <LiveDot lastSync={lastSync} testid="histology-live-indicator" />
          </p>
        </div>
        {canOrder && (
          <Button onClick={() => setOpen(true)} data-testid="new-histology-request-button"
                  className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
            <Plus size={16} className="mr-2" /> New request
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-1" data-testid="histology-kind-filter">
        {[["", "All requests"], ["inhouse", "In-house work"], ["sendout", "Send-outs"]].map(([k, label]) => (
          <button key={k || "all"} onClick={() => setKind(k)} data-testid={`histology-filter-${k || "all"}`}
                  className={`h-10 border px-3 text-sm font-medium ${kind === k ? "border-[#134e4a] bg-[#134e4a] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-[#134e4a]"}`}>
            {label}
          </button>
        ))}
        <Link to="/sendouts" data-testid="histology-to-sendouts-link"
              className="ml-auto inline-flex h-10 items-center border border-slate-300 px-3 text-sm font-medium text-[#134e4a] hover:border-[#134e4a]">
          Send-Outs &amp; Billing ledger →
        </Link>
      </div>

      {BUCKETS.map(({ key, label, icon: Icon }) => {
        const list = rows(key);
        return (
          <section key={key} className="border border-slate-200" data-testid={`histology-section-${key}`}>
            <header className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
              <Icon size={15} className="text-[#134e4a]" />
              <h2 className="text-base font-semibold text-slate-800 md:text-lg">{label}</h2>
              <span className="num ml-auto text-sm font-semibold text-slate-600">{list.length}</span>
            </header>
            {list.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-slate-500">Nothing here.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px] text-sm">
                  <thead className="bg-white text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr className="border-b border-slate-200">
                      <th className="px-3 py-2">Case</th><th className="px-3 py-2">Patient</th>
                      <th className="px-3 py-2">Type</th><th className="px-3 py-2">Request</th>
                      <th className="px-3 py-2">Material</th><th className="px-3 py-2">Destination</th>
                      <th className="px-3 py-2">Status</th><th className="px-3 py-2">Requested</th>
                      <th className="px-3 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100 row-hover hover:bg-slate-50"
                          data-testid={`histology-row-${r.id}`}>
                        <td className="px-3 py-2">
                          <Link to={`/cases/${r.case_id}`} className="font-mono font-semibold text-[#134e4a] hover:underline">
                            {r.accession_number}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-slate-800">{r.patient_name}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold ${r.kind === "sendout" ? "text-sky-900" : "text-[#134e4a]"}`}>
                            {r.kind === "sendout" ? <Truck size={13} /> : <Scissors size={13} />}
                            {r.kind === "sendout" ? "Send-out" : "In-house"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-800">
                          {r.request}
                          {r.priority === "STAT" && (
                            <span className="ml-1.5 bg-red-800 px-1.5 py-0.5 text-[11px] font-bold text-white">STAT</span>
                          )}
                          {r.note && <div className="text-xs text-slate-500">“{r.note}”</div>}
                        </td>
                        <td className="max-w-[220px] px-3 py-2 font-mono text-xs text-slate-700">{r.target}</td>
                        <td className="px-3 py-2 text-slate-700">{r.destination}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[r.status] || "bg-slate-200 text-slate-800"}`}>
                            {r.status}
                          </span>
                          {r.outcome && <div className="font-mono text-xs text-green-800">{r.outcome}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">
                          {r.requested_by || "—"}<br />{fmtDateTime(r.requested_at)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {can("inventory:write") && !["Completed", "Cancelled", "Results received"].includes(r.status) && (
                            <span className="flex flex-wrap justify-end gap-1.5">
                              {r.kind === "inhouse" ? (
                                <>
                                  {r.status === "Pending" && (
                                    <Button size="sm" variant="outline" disabled={busy === r.id}
                                            onClick={() => setInhouseStatus(r, "In progress")}
                                            data-testid={`accept-${r.id}`} className="h-9 border-slate-300">Accept</Button>
                                  )}
                                  <Button size="sm" disabled={busy === r.id} onClick={() => setInhouseStatus(r, "Completed")}
                                          data-testid={`complete-${r.id}`} className="h-9 bg-[#134e4a] hover:bg-[#0f3f3c]">Complete</Button>
                                  <Button size="sm" variant="outline" disabled={busy === r.id}
                                          onClick={() => setInhouseStatus(r, "Cancelled")}
                                          data-testid={`cancel-${r.id}`} className="h-9 border-slate-300">Cancel</Button>
                                </>
                              ) : (
                                <>
                                  {["Requested", "Pending pickup"].includes(r.status) && (
                                    <Button size="sm" disabled={busy === r.id} onClick={() => setSendoutStatus(r, "In transit")}
                                            data-testid={`ship-${r.id}`} className="h-9 bg-[#134e4a] hover:bg-[#0f3f3c]">Mark shipped</Button>
                                  )}
                                  <Link to="/sendouts" data-testid={`manage-sendout-${r.id}`}
                                        className="inline-flex h-9 items-center border border-slate-300 px-2.5 text-xs font-semibold text-[#134e4a] hover:border-[#134e4a]">
                                    Manage
                                  </Link>
                                  <Button size="sm" variant="outline" disabled={busy === r.id}
                                          onClick={() => setSendoutStatus(r, "Cancelled")}
                                          data-testid={`cancel-${r.id}`} className="h-9 border-slate-300">Cancel</Button>
                                </>
                              )}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })}

      <RequestDialog open={open} onOpenChange={setOpen} cases={cases} onDone={load}
                     canOrderInhouse={data.can_order_inhouse} canOrderSendout={data.can_order_sendout} />
    </div>
  );
}
